import { useEffect, useRef } from 'react'
import type { BirdiePhase } from './boardPhase'
import { ringDelaysForLive } from './livePutts'
import { RING_STAGGER_MS } from './settlement'
import type { TeeOffBeat } from './teeOff'
import type { PuttMark } from './tileViewModel'

/**
 * Birdie's cues (B23) — what the board SOUNDS at each moment it already shows.
 *
 * Every cue rides a state the board has already derived for its picture, on
 * the same held timeline: a putt sounds when its ring is drawn (never when
 * the socket row lands), the win and the loss when the settlement is on
 * screen. So a cue can never lead the video by more than the picture does.
 * Frost and fire have no cue here on purpose: the skin lands on the socket row
 * while the picture is still behind, so only the clip's own audio can be in
 * sync with the green (owner 2026-09-21).
 *
 * `birdieCues` is the whole rule, as a pure diff of two snapshots, so every
 * moment is a table test; `useBirdieCues` is the thin scheduler around it.
 */
export type BirdieCue =
  | 'birdieRoundOpen'
  | 'birdieTick'
  | 'birdiePuttHoled'
  | 'birdiePuttMissed'
  | 'birdieWin'
  /** a birdie: all three dropped — a small songbird, after the third ball's own drop (the boss) */
  | 'birdieBirdie'
  /** an eagle: all three dropped on a hard card — rarer, a bigger bird (make chance at most EAGLE_MAKE_RATE) */
  | 'birdieEagle'
  /** the shared strike, which the chip-placement panel plays at the reveal and Birdie never wired */
  | 'lightningRound'

/**
 * Golf's own words for it. A birdie is one under par; an eagle is rarer still. Here the three balls are
 * the hole: all three in is the birdie, and all three in for a golfer the card gives at most this make
 * chance is the eagle. Anything short of a clean sweep gets no bird, so the bird stays a detail — a 70%
 * card sweeps about one round in three, a 45% card about one in eleven (owner 2026-09-22).
 */
export const EAGLE_MAKE_RATE = 0.5
/** After the third ring's own drop, so the ball is heard before the bird. */
export const BIRD_AFTER_DROP_MS = 450

export interface BirdieCueState {
  raceId?: string
  phase: BirdiePhase
  /** the tee-off pill's beat, undefined while the pill is not up */
  teeOffBeat?: TeeOffBeat
  /** the rings the card draws right now — `boardPhase().livePutts` */
  liveMarks: readonly PuttMark[]
  settlementVisible: boolean
  /** the player has chips on this round */
  hasBets: boolean
  /** …and at least one of them paid — the only settlement that sounds */
  won: boolean
  /** the lightning boost is revealed with its boosted options */
  lightningRevealed: boolean
  /** the card's make chance, 0..1 — decides birdie vs eagle; undefined means no eagle, birdie only */
  makeRate?: number
}

export interface ScheduledCue {
  cue: BirdieCue
  delayMs: number
}

/**
 * The cues that fire on the step from `prev` to `next`. Nothing fires without a
 * previous snapshot (a page opened mid-round does not burst three putts and a
 * result at the viewer), and nothing carries across a race change except the
 * new round's own opening.
 */
export function birdieCues(prev: BirdieCueState | undefined, next: BirdieCueState): ScheduledCue[] {
  const out: ScheduledCue[] = []
  if (prev === undefined) return out
  const sameRace = prev.raceId === next.raceId

  if (next.phase === 'open' && (!sameRace || prev.phase !== 'open')) {
    out.push({ cue: 'birdieRoundOpen', delayMs: 0 })
  }
  if (!sameRace) return out

  // The three counted beats tick; the held `ready` beat is silence (owner: no go sound).
  if (next.teeOffBeat !== undefined && next.teeOffBeat !== 'ready' && next.teeOffBeat !== prev.teeOffBeat) {
    out.push({ cue: 'birdieTick', delayMs: 0 })
  }

  if (!prev.lightningRevealed && next.lightningRevealed) {
    out.push({ cue: 'lightningRound', delayMs: 0 })
  }

  if (!next.settlementVisible) {
    const before = prev.liveMarks.length
    const after = next.liveMarks.length
    if (after > before) {
      // The same stagger the rings animate with, so a catch-up burst sounds
      // like it looks: one ring, one sound, a quarter second apart.
      const delays = ringDelaysForLive(after, before, RING_STAGGER_MS)
      for (let i = before; i < after; i++) {
        out.push({
          cue: next.liveMarks[i] === 'holed' ? 'birdiePuttHoled' : 'birdiePuttMissed',
          delayMs: delays[i] ?? 0
        })
      }
      // The third ring completes a clean sweep: the bird, after that ball's own drop.
      if (after === 3 && next.liveMarks.every((mark) => mark === 'holed')) {
        const eagle = next.makeRate !== undefined && next.makeRate <= EAGLE_MAKE_RATE
        out.push({ cue: eagle ? 'birdieEagle' : 'birdieBirdie', delayMs: (delays[2] ?? 0) + BIRD_AFTER_DROP_MS })
      }
    }
  }

  // A win is the gallery; a loss is silence, as on every other game (owner).
  if (!prev.settlementVisible && next.settlementVisible && next.hasBets && next.won) {
    out.push({ cue: 'birdieWin', delayMs: 0 })
  }

  return out
}

/**
 * Plays the cues for every change of `state`. Delayed cues are timers that a
 * race change or an unmount clears — a ring that will never be drawn must not
 * still sound.
 */
export function useBirdieCues(state: BirdieCueState, play: (cue: BirdieCue) => void): void {
  const prevRef = useRef<BirdieCueState | undefined>(undefined)
  const timersRef = useRef<number[]>([])
  const {
    raceId,
    phase,
    teeOffBeat,
    liveMarks,
    settlementVisible,
    hasBets,
    won,
    lightningRevealed,
    makeRate
  } = state

  useEffect(() => {
    const next: BirdieCueState = {
      ...(raceId === undefined ? {} : { raceId }),
      phase,
      ...(teeOffBeat === undefined ? {} : { teeOffBeat }),
      liveMarks,
      settlementVisible,
      hasBets,
      won,
      lightningRevealed,
      ...(makeRate === undefined ? {} : { makeRate })
    }
    const cues = birdieCues(prevRef.current, next)
    prevRef.current = next
    for (const { cue, delayMs } of cues) {
      if (delayMs <= 0) {
        play(cue)
      } else {
        const id = window.setTimeout(() => {
          timersRef.current = timersRef.current.filter((t) => t !== id)
          play(cue)
        }, delayMs)
        timersRef.current.push(id)
      }
    }
  }, [
    raceId,
    phase,
    teeOffBeat,
    liveMarks,
    settlementVisible,
    hasBets,
    won,
    lightningRevealed,
    makeRate,
    play
  ])

  useEffect(
    () => () => {
      for (const id of timersRef.current) window.clearTimeout(id)
      timersRef.current = []
    },
    [raceId]
  )
}
